import { ChevronLeftOutlineIcon, ChevronRightOutlineIcon } from '@applyboard/ui-icons';
import React, { useRef } from 'react';
import { Button } from '../Button';
import { Flex } from '../Flex';
import { Text } from '../Text';
import { Select } from '../Select';
import { SrOnly } from '../SrOnly';
import { useFullTheme } from '~/design';
import { InternalButton, useButtonSizeStyle } from '~/internal/InternalButton';
import { useCurrentBreakpoint } from '~/utils';
import { flushSync } from 'react-dom';

export interface PaginationProps {
  currentPage: number;
  disabled?: boolean;
  onPageChange: (value: number) => void;
  onPageSizeChange?: (value: number) => void;
  numItems: number;
  pageSize: number;
  pageSizeOptions?: number[];
  variant?: 'items';
}

/**
 * Pagination allows you to divide large amounts of content into smaller chunks across multiple
 * pages. It helps users navigate between large groups of content separated by pages.
 *
 * This component is _not_ designed to work the same on very small screens. It is optimized for
 * larger screens, and an alternative UX is recommended.
 */
export function Pagination({
  currentPage: _currentPage,
  disabled = false,
  onPageChange,
  numItems,
  pageSize,
  variant,
  onPageSizeChange,
  pageSizeOptions,
}: PaginationProps) {
  const pages = Math.ceil(numItems / pageSize);
  const currentPage = Math.max(Math.min(_currentPage ?? 1, pages), 1);
  const currentBreakpoint = useCurrentBreakpoint();
  const theme = useFullTheme();
  const isSmallScreen = ['xs', 'sm'].includes(currentBreakpoint ?? 'xs');
  const isExtraSmallScreen = ['xs'].includes(currentBreakpoint ?? 'xs');
  const btnSize = isSmallScreen ? 'sm' : 'md';
  const sizeStyle = useButtonSizeStyle({ size: btnSize });
  const containerRef = useRef<HTMLUListElement>(null);

  if (variant === 'items') {
    return (
      <ItemsPagination
        currentPage={currentPage}
        onPageChange={onPageChange}
        disabled={disabled}
        pageSize={pageSize}
        numItems={numItems}
        onPageSizeChange={onPageSizeChange}
        pageSizeOptions={pageSizeOptions}
      />
    );
  }

  let btns = [];

  if (isExtraSmallScreen) {
    if (pages <= 5) {
      btns = fillArray(1, pages);
    } else if (currentPage <= 3) {
      btns = [...fillArray(1, 3), undefined, pages];
    } else if (currentPage >= pages - 2) {
      btns = [1, undefined, ...fillArray(pages - 2, pages)];
    } else {
      btns = [1, undefined, currentPage, undefined, pages];
    }
  } else if (pages <= 7) {
    btns = fillArray(1, pages);
  } else {
    if (currentPage <= 5) {
      btns = [...fillArray(1, 5), undefined, pages];
    } else if (currentPage >= pages - 4) {
      btns = [1, undefined, ...fillArray(pages - 4, pages)];
    } else {
      btns = [1, undefined, ...fillArray(currentPage - 1, currentPage + 1), undefined, pages];
    }
  }

  const liSpacing = isSmallScreen ? theme.spacing[1] : theme.spacing[2];

  return (
    <nav aria-label="pagination">
      <ul
        css={{
          display: 'flex',
          listStyleType: 'none',
          margin: 0,
          padding: 0,
        }}
        ref={containerRef}
      >
        <li css={{ marginRight: liSpacing }}>
          <Button
            aria-label="Previous page"
            disabled={disabled || currentPage === 1}
            emphasis="highlighted"
            intent="secondary"
            leadIcon={ChevronLeftOutlineIcon}
            onClick={() => {
              const newPage = currentPage - 1;
              flushSync(() => onPageChange(newPage));
              if (newPage === 1) {
                containerRef.current?.querySelectorAll('button')?.[1]?.focus();
              }
            }}
            size={btnSize}
          />
        </li>

        {btns.map((number, idx) => {
          const isBtn = !!number;
          const isSelected = isBtn && number === currentPage;
          if (!isBtn) {
            return (
              <li key={`ellipsis-${idx}`}>
                <div css={[sizeStyle, { alignItems: 'center', display: 'flex' }]}>...</div>
              </li>
            );
          }
          return (
            <li key={number}>
              <InternalButton
                aria-current={isSelected ? 'true' : 'false'}
                aria-label={`Go to page ${number}`}
                disabled={disabled}
                emphasis={isSelected ? 'highlighted' : 'transparent'}
                intent={isSelected ? 'primary' : 'secondary'}
                onClick={() => onPageChange(number)}
                size={btnSize}
              >
                {number.toString()}
              </InternalButton>
            </li>
          );
        })}

        <li css={{ marginLeft: liSpacing }}>
          <Button
            aria-label="Next page"
            disabled={disabled || currentPage === pages}
            emphasis="highlighted"
            intent="secondary"
            leadIcon={ChevronRightOutlineIcon}
            onClick={() => {
              const newPage = currentPage + 1;
              flushSync(() => onPageChange(newPage));
              if (newPage === pages) {
                const btns = containerRef.current?.querySelectorAll('button');
                if (btns) {
                  btns[btns.length - 2]?.focus();
                }
              }
            }}
            size={btnSize}
          />
        </li>
      </ul>
    </nav>
  );
}

const fillArray = (start: number, end: number) =>
  new Array(end - start + 1).fill(0).map((_, idx) => start + idx);

function ItemsPagination({
  currentPage,
  pageSize,
  onPageChange,
  numItems,
  disabled,
  pageSizeOptions,
  onPageSizeChange,
}: {
  currentPage: number;
  disabled?: boolean;
  onPageChange: (value: number) => void;
  pageSize: number;
  numItems: number;
  pageSizeOptions?: number[];
  onPageSizeChange?: (value: number) => void;
}) {
  const lastPage = Math.ceil(numItems / pageSize);
  const prevPageBtnRef = useRef<HTMLButtonElement>(null);
  const nextPageBtnRef = useRef<HTMLButtonElement>(null);

  return (
    <div role="region" aria-label="Pagination">
      <Flex justify="between" align="center">
        <Flex align="center" gap={4}>
          <Flex gap={2} align="center">
            <Select
              appearance="native"
              disabled={disabled}
              label="Items per page:"
              labelPosition="start"
              onChange={value => {
                onPageChange(1);
                onPageSizeChange?.(Number(value));
              }}
              placeholder=""
              value={pageSize.toString()}
            >
              {(pageSizeOptions || [20, 25, 50, 100]).map(item => (
                <Select.Option
                  key={item.toString()}
                  label={item.toString()}
                  value={item.toString()}
                />
              ))}
            </Select>
          </Flex>
          <Text>
            <span aria-live="polite" aria-atomic data-testid="temp">
              {numItems === 0 ? 0 : (currentPage - 1) * pageSize + 1}
              <span aria-hidden> - </span>
              <SrOnly>to </SrOnly>
              {Math.min(numItems, currentPage * pageSize)} of {numItems} items
            </span>
          </Text>
        </Flex>
        <Flex gap={4} align="center">
          <Flex gap={2} align="center">
            <Text>
              {numItems === 0 ? 0 : currentPage} of {lastPage} pages
            </Text>
          </Flex>
          <Flex gap={2} align="center">
            <Button
              aria-label="Previous page"
              intent="secondary"
              leadIcon={ChevronLeftOutlineIcon}
              onClick={() => {
                const newPage = currentPage - 1;
                flushSync(() => onPageChange(newPage));
                if (newPage === 1) {
                  nextPageBtnRef.current?.focus();
                }
              }}
              disabled={currentPage <= 1 || disabled}
              emphasis="highlighted"
              ref={prevPageBtnRef}
            />
            <Button
              aria-label="Next page"
              intent="secondary"
              leadIcon={ChevronRightOutlineIcon}
              onClick={() => {
                const newPage = currentPage + 1;
                flushSync(() => onPageChange(newPage));
                if (newPage === lastPage) {
                  prevPageBtnRef.current?.focus();
                }
              }}
              disabled={currentPage >= lastPage || disabled}
              emphasis="highlighted"
              ref={nextPageBtnRef}
            />
          </Flex>
        </Flex>
      </Flex>
    </div>
  );
}
